import { MarkdownText } from "@/components/assistant-ui/markdown-text";
import { Avatar, AvatarFallback } from "@/components/ui/avatar.tsx";
import {
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useThread,
} from "@assistant-ui/react";
import { BotIcon, BugIcon, SendIcon, StopCircleIcon } from "lucide-react";
import { useContext } from "react";
import { AvaChatHeader } from "./ava-chat-header.tsx";
import { ToolDispatcher } from "./tool-uis/tool-dispatcher";
import { DebugContext } from "@/routes/_authenticated/ava/$chatId";

interface AvaChatProps {
  chatId: string;
}

// Helper function to wrap tool components with status extraction
function createToolComponent(props: any) {
  const extractedStatus = props.status?.type || props.status;
  return <DynamicToolCall {...props} status={extractedStatus} />;
}

// Dynamic tool call component with collapsible design
function DynamicToolCall(props: any) {
  const { isDebugMode } = useContext(DebugContext);

  if (isDebugMode) {
    // Debug mode: show full technical details
    return (
      <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <div className="mb-2 flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded bg-amber-500 text-xs font-semibold text-white">
            🐛
          </div>
          <span className="text-sm font-medium text-amber-800">
            Tool Debug: {props.toolName}
          </span>
        </div>
        <div className="text-sm text-amber-700">
          <pre className="overflow-auto rounded bg-amber-100 p-2 text-xs">
            {JSON.stringify(props, null, 2)}
          </pre>
        </div>
      </div>
    );
  }

  // Production mode: use collapsible tool dispatcher
  return (
    <div className="mb-2">
      <ToolDispatcher
        toolName={props.toolName}
        args={props.args}
        result={props.result}
        status={props.status}
        _toolCallId={props.toolCallId}
      />
    </div>
  );
}

// Custom Text component that wraps content in a gray bubble
function TextBubble(props: any) {
  return (
    <div className="mb-2 rounded-md bg-gray-100 px-4 py-2.5 text-sm text-gray-900">
      <MarkdownText {...props} />
    </div>
  );
}

// Styled message components that preserve assistant-ui functionality
function UserMessage() {
  return (
    <MessagePrimitive.Root className="flex w-full justify-end py-2">
      <div className="max-w-[80%] break-words rounded-md bg-blue-500 px-4 py-2.5 text-white">
        <MessagePrimitive.Parts />
      </div>
    </MessagePrimitive.Root>
  );
}

function AssistantMessage() {
  return (
    <MessagePrimitive.Root className="flex w-full justify-start py-2">
      <div className="flex max-w-[90%] gap-3">
        <Avatar className="mt-1 h-8 w-8 flex-shrink-0">
          <AvatarFallback className="border border-gray-200 bg-gray-100">
            <BotIcon className="h-4 w-4 text-gray-600" />
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-2">
          <MessagePrimitive.Parts
            components={{
              Text: TextBubble,
              tools: {
                by_name: {
                  // User Tools
                  search_users: createToolComponent,
                  get_user_profile: createToolComponent,
                  get_mentor_slots: createToolComponent,
                  get_all_mentor_slots: createToolComponent,

                  // Mentorship Tools
                  get_mentorship_details: createToolComponent,
                  get_mentorships_by_teen: createToolComponent,
                  get_mentorships_by_mentor: createToolComponent,
                  get_mentorship_history_for_teen: createToolComponent,
                  get_all_mentorships_for_user: createToolComponent,

                  // Insights Tools
                  get_teen_insights: createToolComponent,
                  get_insights_by_engagement_status: createToolComponent,
                  get_insights_needing_followup: createToolComponent,
                  get_recent_insights_for_mentorship: createToolComponent,
                  find_similar_insights: createToolComponent,

                  // Scoring Tools
                  get_mentor_score: createToolComponent,
                  compare_mentors: createToolComponent,
                  get_top_mentors_by_score: createToolComponent,
                  get_mentor_score_statistics: createToolComponent,
                  get_daily_average_retention_rates: createToolComponent,

                  // Message Tools
                  get_notes_by_mentorship: createToolComponent,
                  get_recent_messages: createToolComponent,
                  get_chat_history: createToolComponent,
                  search_messages_by_text: createToolComponent,
                  find_similar_messages: createToolComponent,
                  get_conversation_summary: createToolComponent,
                  get_messages_from_last_week: createToolComponent,

                  // Escalation Tools
                  get_all_escalations: createToolComponent,
                  get_escalations_by_mentee: createToolComponent,
                  get_escalations_by_mentor: createToolComponent,

                  // Survey Tools
                  search_teen_memories: createToolComponent,
                  get_teen_memories: createToolComponent,
                  get_available_survey_types: createToolComponent,
                  get_recent_survey_summary: createToolComponent,
                  search_survey_summaries: createToolComponent,
                  search_surveys_by_type: createToolComponent,
                },

                // Fallback for unknown tools - format tool name nicely
                Fallback: (props) => {
                  const extractedStatus = props.status?.type || props.status;
                  return (
                    <DynamicToolCall {...props} status={extractedStatus} />
                  );
                },
              },
            }}
          />
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

// Composer component with cancel/stop support
function Composer() {
  const { isRunning } = useThread();

  return (
    <div className="border-t bg-white p-4">
      <ComposerPrimitive.Root className="flex items-end gap-3">
        <ComposerPrimitive.Input
          placeholder="Type your message..."
          className="flex-1 resize-none rounded-md border border-gray-300 px-4 py-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          rows={1}
          maxRows={4}
        />

        {/* Show Send button when NOT running */}
        {!isRunning && (
          <ComposerPrimitive.Send asChild>
            <button className="flex h-11 w-11 items-center justify-center rounded-md bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              <SendIcon className="h-5 w-5" />
            </button>
          </ComposerPrimitive.Send>
        )}

        {/* Show Cancel button when running */}
        {isRunning && (
          <ComposerPrimitive.Cancel asChild>
            <button className="flex h-11 w-11 items-center justify-center rounded-md bg-red-500 text-white hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2">
              <StopCircleIcon className="h-5 w-5" />
            </button>
          </ComposerPrimitive.Cancel>
        )}
      </ComposerPrimitive.Root>
    </div>
  );
}

export function AvaChat({ chatId }: AvaChatProps) {
  const { isDebugMode, toggleDebug } = useContext(DebugContext);

  const hideDebugToggle =
    import.meta.env.VITE_STYTCH_PUBLIC_ENV === "production";

  return (
    <div className="flex h-full flex-col bg-white">
      <AvaChatHeader chatId={chatId} />

      {/* Debug toggle button */}
      {!hideDebugToggle && (
        <div className="border-b border-gray-200 px-4 py-2">
          <button
            onClick={toggleDebug}
            className={`flex items-center gap-2 rounded-lg px-3 py-1 text-xs font-medium transition-colors ${
              isDebugMode
                ? "bg-orange-100 text-orange-800 hover:bg-orange-200"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            <BugIcon className="h-3 w-3" />
            {isDebugMode ? "Debug Mode: ON" : "Debug Mode: OFF"}
          </button>
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        <ThreadPrimitive.Root className="flex h-full flex-col">
          <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto px-4 py-6">
            {/* Empty state for new conversations */}
            <ThreadPrimitive.Empty>
              <div className="flex h-full items-center justify-center">
                <div className="text-center">
                  <div className="mb-2">
                    <BotIcon className="mx-auto h-12 w-12 text-gray-400" />
                  </div>
                  <p className="text-gray-500">Start a conversation with AVA</p>
                  <p className="mt-1 text-sm text-gray-400">
                    Ask me anything about mentoring and youth development
                  </p>
                </div>
              </div>
            </ThreadPrimitive.Empty>

            {/* All messages with proper chat styling */}
            <ThreadPrimitive.Messages
              components={{
                UserMessage,
                AssistantMessage,
              }}
            />
          </ThreadPrimitive.Viewport>

          {/* Composer inside ThreadPrimitive.Root for access to thread state */}
          <Composer />
        </ThreadPrimitive.Root>
      </div>
    </div>
  );
}
